/**
 * Image Proxy Utility for TakeFav
 * Handles CORS issues with external images by proxying them through the backend
 */

/**
 * Check if an image URL needs CORS proxying
 */
export function needsCorsProxying(url: string): boolean {
  if (!url) return false
  
  // Local URLs (relative paths, data URIs, or localhost) don't need proxying
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../') || 
      url.startsWith('data:') || url.includes('localhost')) {
    return false
  }
  
  // For staging and production environments, don't proxy images
  // Let the browser handle CORS naturally (most external images work fine)
  const isStaging = window.location.hostname === 'staging.takefav.com'
  const isProduction = window.location.hostname === 'www.takefav.com' || window.location.hostname === 'takefav.com'
  
  if (isStaging || isProduction) {
    return false
  }
  
  // Only proxy for localhost development where CORS is more restrictive
  const corsRestrictedDomains = [
    'aliexpress.com',
    'ae-pic-a1.aliexpress-media.com',
    // Amazon and eBay images don't have CORS issues - no need to proxy
    // 'amazon.com',
    // 'm.media-amazon.com', 
    // 'ebay.com',
    // 'i.ebayimg.com',
    // 'thumbs.ebaystatic.com'
  ]
  
  try {
    const urlObj = new URL(url)
    return corsRestrictedDomains.some(domain => urlObj.hostname.includes(domain))
  } catch {
    // If URL parsing fails, it's likely a relative path, so it's local
    return false
  }
}

/**
 * Get proxied image URL for external images
 */
export function getProxiedImageUrl(imageUrl: string): string {
  if (!imageUrl) return ''
  
  // If it's an image that needs CORS proxying, proxy it through the backend
  if (needsCorsProxying(imageUrl)) {
    // Only localhost needs proxying - staging/production handle CORS naturally
    return `/api/proxy/image?url=${encodeURIComponent(imageUrl)}`
  }
  
  // If it's a local image, return as-is
  return imageUrl
}

/**
 * Get the best available image URL for a product with automatic proxying
 */
export function getProductImageUrl(product: any): string {
  if (!product) return ''
  
  // Priority: imageUrl > image > fallback
  const imageUrl = product.imageUrl || product.image || ''
  
  if (!imageUrl) return ''
  
  // Automatically proxy external images to avoid CORS issues
  return getProxiedImageUrl(imageUrl)
}
